import { useEffect, useState, useCallback } from 'react';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import { useElementSize } from '../../CustomHooks';
import StandardCard from '../StandardCard';
import StandardsByUnit from '../StandardsByUnit';
import {
  getAllApplicableStandards,
  getUniqueMetStandards,
  getCompletedLessonsWithStandards,
  getIncompleteLessonsWithStandards,
  getCompleteAndIncompleteByUnit,
} from '../../CurriculumMiddleSchoolDashboard/completionHelpers';
import { getCoreStandardsObject, CoreStandard } from '../../core/context/contextHelpers';
import styles from './StandardsDashboard.module.scss';
import { CompletionStatus, ELAStandardsMap } from '@peekapak/types/LessonPlanTypes';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';

interface Props {
  completionStatus: CompletionStatus;
  standardsMap: ELAStandardsMap;
  handleResize: () => void;
  classroom: ClassroomType;
  grade: number;
}

const StandardsDashboard = ({ completionStatus, standardsMap, handleResize, classroom, grade }: Props): JSX.Element => {
  const [isStandardsToggle, setIsStandardsToggle] = useStateWithCallbackLazy(true);
  const [standardsLibrary, setStandardsLibrary] = useState<{
    [key: string]: CoreStandard;
  }>();
  const [_, setExpandTrigger] = useStateWithCallbackLazy(false);
  const [contentHeight, setContentHeight] = useState('0px');
  const [standardsRef, { height }] = useElementSize();
  const handleHeightChange = () => {
    setExpandTrigger(
      (prevState) => !prevState,
      () => handleResize(),
    );
  };

  const handleStandardsToggle = (isStandards: boolean) => {
    setIsStandardsToggle(isStandards, () => handleResize());
  };

  const allStandards = getAllApplicableStandards(standardsMap, grade);
  const uniqueStandards = [...new Set(allStandards)];

  const completedLessons = getCompletedLessonsWithStandards(completionStatus, standardsMap, grade);

  const completedStandards = getUniqueMetStandards(completionStatus, standardsMap, grade);

  const incompleteStandards = uniqueStandards.filter((item) => !completedStandards.includes(item));

  const incompleteLessons = getIncompleteLessonsWithStandards(completionStatus, standardsMap, grade);

  const standardsByUnit = getCompleteAndIncompleteByUnit(completedLessons, incompleteLessons, grade);

  const getStandardDescriptions = useCallback(async () => {
    const coreStandards = await getCoreStandardsObject();
    setStandardsLibrary(coreStandards);
  }, []);

  useEffect(() => {
    getStandardDescriptions();
  }, []);

  useEffect(() => {
    standardsRef.current && setContentHeight(`${standardsRef.current.scrollHeight}px`);
    handleResize();
  }, [height, contentHeight, classroom, standardsLibrary]);

  return (
    <div className={styles.container}>
      <div className={styles.toggleBar}>
        <div
          className={styles.toggleOption}
          style={{
            borderBottom: isStandardsToggle ? '3px solid #00afd8' : '',
            color: isStandardsToggle ? '#012a73' : 'inherit',
          }}
          onClick={() => handleStandardsToggle(true)}
        >
          By Standard
        </div>
        <div
          className={styles.toggleOption}
          style={{
            borderBottom: !isStandardsToggle ? '3px solid #00afd8' : '',
            color: !isStandardsToggle ? '#012a73' : 'inherit',
          }}
          onClick={() => handleStandardsToggle(false)}
        >
          By Unit
        </div>
      </div>
      {isStandardsToggle ? (
        <div className={styles.byStandardContainer} ref={standardsRef}>
          <div className={styles.standardsList}>
            <h3 className={styles.title}>Completed Standards</h3>
            {standardsLibrary &&
              completedStandards.map((standard) => {
                return (
                  <StandardCard
                    key={standard}
                    name={standardsLibrary[standard].label}
                    description={standardsLibrary[standard].text}
                    lessons={completedLessons.filter((item) => item.standards.includes(standard))}
                    classroom={classroom}
                    passExpandToParent={handleHeightChange}
                    isComplete
                  />
                );
              })}
          </div>
          <div className={styles.standardsList}>
            <h3 className={styles.title}>Incomplete Standards</h3>
            {standardsLibrary &&
              incompleteStandards.map((standard) => {
                return (
                  <StandardCard
                    key={standard}
                    name={standardsLibrary[standard].label}
                    description={standardsLibrary[standard].text}
                    lessons={incompleteLessons.filter((item) => item.standards.includes(standard))}
                    classroom={classroom}
                    passExpandToParent={handleHeightChange}
                  />
                );
              })}
          </div>
        </div>
      ) : (
        standardsLibrary && (
          <StandardsByUnit
            colA='Unit Name'
            colB='Standards Completion'
            colC='Complete'
            colD='Incomplete'
            data={standardsByUnit}
            barColor='#012a73'
            isShouldAnimate
            standardsLibrary={standardsLibrary}
          />
        )
      )}
    </div>
  );
};

export default StandardsDashboard;
